import type { ColorScheme } from '@/lib/colorSchemes';
import { COLOR_SCHEMES } from '@/lib/colorSchemes';
import type { HandData } from '@/types/chart';

interface ColorSwatchProps {
  schemeKey: keyof typeof COLOR_SCHEMES;
  width?: number;
  height?: number;
}

/**
 * Sample representative colors from a color scheme for preview
 */
function sampleColors(scheme: ColorScheme, schemeKey: string): string[] {
  // Mock hand data for getting colors (we only care about the type)
  const mockHandData: HandData = {
    hand: 'AA',
    row: 0,
    col: 0,
    type: 'pair',
    subtype: 'premium-pair',
    segments: [],
  };

  // Type-based: show colors for each type
  if (schemeKey === 'TYPE_BASED_DEFAULT') {
    const pairColor = scheme.getColor({ ...mockHandData, type: 'pair' }, 0.5);
    const suitedColor = scheme.getColor({ ...mockHandData, type: 'suited' }, 0.5);
    const offsuitColor = scheme.getColor({ ...mockHandData, type: 'offsuit' }, 0.5);
    return [pairColor, suitedColor, offsuitColor];
  }

  // Categorical: show first 3-4 colors
  if (schemeKey.startsWith('CATEGORICAL_')) {
    const count = Number.parseInt(schemeKey.split('_')[1], 10);
    const samples = Math.min(4, count);
    const colors: string[] = [];
    for (let i = 0; i < samples; i++) {
      // Sample evenly across the range
      const normalized = (i + 1) / count;
      colors.push(scheme.getColor(mockHandData, normalized));
    }
    return colors;
  }

  // GTO Actions: show fold, call, raise colors
  if (schemeKey === 'GTO_ACTIONS') {
    return [
      scheme.getColor(mockHandData, 0, 'Fold'),
      scheme.getColor(mockHandData, 0, 'Call'),
      scheme.getColor(mockHandData, 0, 'Raise'),
      scheme.getColor(mockHandData, 0, 'All-in'),
    ];
  }

  // Heatmaps: sample at 0, 0.5, 1.0
  return [
    scheme.getColor(mockHandData, 0.0),
    scheme.getColor(mockHandData, 0.25),
    scheme.getColor(mockHandData, 0.5),
    scheme.getColor(mockHandData, 0.75),
    scheme.getColor(mockHandData, 1.0),
  ];
}

export default function ColorSwatch({ schemeKey, width = 24, height = 24 }: ColorSwatchProps) {
  const scheme = COLOR_SCHEMES[schemeKey];
  const colors = sampleColors(scheme, schemeKey);

  const barWidth = width / colors.length;

  return (
    <svg
      width={width}
      height={height}
      style={{ borderRadius: 2, overflow: 'hidden' }}
      role="img"
      aria-label="Color scheme preview"
    >
      {colors.map((color, index) => (
        <rect key={color} x={index * barWidth} y={0} width={barWidth} height={height} fill={color} />
      ))}
    </svg>
  );
}
